import { MinPriorityQueue } from "@datastructures-js/priority-queue"
import type { POIncomingOrder } from "~/utils/incomingOrdersUtils"
import type { StockStatus } from "~/utils/outOfStockDatesUtils"
import { sumBy } from "lodash"
import { groupBy } from "~/utils/tsUtils"

export type SkuTag = {
  id: number
  name: string
}

function getNextValue(
  quantity: number,
  colliSize: number,
  minimumOrderQuantity: number,
  value: number,
  isSubtracting: boolean,
) {
  const addValue = isSubtracting ? -value : value
  let nextValue = quantity + addValue
  if (nextValue < minimumOrderQuantity) {
    nextValue = isSubtracting ? 0 : minimumOrderQuantity
  }
  nextValue = isSubtracting
    ? Math.floor(nextValue / colliSize) * colliSize
    : Math.ceil(nextValue / colliSize) * colliSize

  return nextValue
}

export type PerWarehouseSkuData = {
  stockLevel: number
  incomingStock: number
  incomingOrders: POIncomingOrder[]
  locationMinimumStockLevel: number
  availableToSell?: number
}

export type POSkuData = {
  skuId: string
  supplierSku: string | null
  variantId: string
  productId: string
  productName: string
  variantName: string | null
  color: string | null
  size: string | null
  sizeDimension1: string | null
  sizeDimension2: string | null
  costPrice: number | null
  imageUrl: string | null
  category: string | null
  gender: string | null
  brand: string | null
  tags: SkuTag[]
  perWarehouse: Map<string, PerWarehouseSkuData>
  extraData: unknown
  /** Raw per-SKU extra data (unmerged) — use when a variant-level fallback must be avoided. */
  extraDataSku: Record<string, unknown> | null
  collections: { id: string; name: string; extraData: Record<string, any>; out?: boolean }[]
  sizeSplitTrust?: string | null
  sizeSplitWeight?: number | null
  variantStockoutImputedFraction?: number | null
  skuStockoutImputedFraction?: number | null
  abcPercentile?: number | null
  settings?: {
    skuId: string
    activatedFrom: Date | null
    userId: string | null
    productType: string | null
    bufferType: string
    bufferValue: number
    totalLeadTimeDays: number
    purchaseIntervalDays: number
    colliSize: number
    minimumOrderQuantity: number
    secondaryMoq: number
    lifecycleStatus: string | null
    ignore: boolean
  }
}

export type IPurchaseOrderItem = POSkuData & {
  quantity: number
  item: { skuId: string; warehouseId: string; quantity: number }
  index: number
  warehouseId: string
  suggestedOrderQuantity: number
  suggestedOrderRawQuantity: number
  secondaryMoqGrouper: string
  forecastsPurchasePeriod: number
  preordersPurchasePeriod: number
  projectedStockAtArrival?: number
  weeksInPurchasePeriod?: number
  outOfStockInformation:
    | {
        variantId: string
        warehouseId: string
        stockoutDates: Date[]
        safetyStockOutDate: Date | undefined
        buffer: number
        status: StockStatus
        suggestedOrder: number
        projectedUnderstock: number
        missingStockEndOfPeriod: number
        stockOutPeriods: [][]
        weeksOnHand: number | undefined
      }
    | undefined
  purchasePeriodLostSales?: number
}

function findIncrementOrderOnItemLevel(
  items: {
    id: string
    colliSize: number
    suggestedOrder: number
    forecast: number
    minimumOrderQuantity: number
    projectedStockAtArrival: number
    weeksInPurchasePeriod: number
  }[],
  quantities: Record<string, number>,
  respectMoq: boolean,
  ignoreColliSize: boolean,
) {
  let factor = Number.POSITIVE_INFINITY
  let id = ""
  let newValue = 0
  for (const item of items) {
    const nextValue = getNextValue(
      quantities[item.id] ?? 0,
      ignoreColliSize ? 1 : item.colliSize,
      respectMoq ? item.minimumOrderQuantity : 1,
      1,
      false,
    )

    let newFactor: number
    const weeklyDemand =
      item.weeksInPurchasePeriod > 0 ? item.forecast / item.weeksInPurchasePeriod : 0

    if (weeklyDemand > 0) {
      // Balance by projected weeks on hand: give units to SKUs with lowest weeks on hand
      const projectedStock = item.projectedStockAtArrival + nextValue
      newFactor = projectedStock / weeklyDemand
    } else {
      // No demand = infinite weeks on hand, deprioritize for receiving more units
      newFactor = Number.POSITIVE_INFINITY
    }

    if (newFactor <= factor) {
      factor = newFactor
      id = item.id
      newValue = nextValue
    }
  }
  return { id, newValue, factor }
}

function findDecrementOrderOnItemLevel(
  items: {
    id: string
    quantity: number
    colliSize: number
    suggestedOrder: number
    forecast: number
    minimumOrderQuantity: number
    projectedStockAtArrival: number
    weeksInPurchasePeriod: number
  }[],
  quantities: Record<string, number>,
  respectMoq: boolean,
  ignoreColliSize: boolean,
) {
  let factor = Number.NEGATIVE_INFINITY
  let id = ""
  let newValue = 0

  for (const item of items.filter((item) => quantities[item.id])) {
    const nextValue = getNextValue(
      quantities[item.id] ?? 0,
      ignoreColliSize ? 1 : item.colliSize,
      respectMoq ? item.minimumOrderQuantity : 1,
      1,
      true,
    )

    let newFactor: number
    const weeklyDemand =
      item.weeksInPurchasePeriod > 0 ? item.forecast / item.weeksInPurchasePeriod : 0

    if (weeklyDemand > 0) {
      // Balance by projected weeks on hand: remove from SKUs with highest weeks on hand
      const projectedStock = item.projectedStockAtArrival + nextValue
      newFactor = projectedStock / weeklyDemand
    } else {
      // No demand = infinite weeks on hand, prioritize for removal
      newFactor = Number.POSITIVE_INFINITY
    }

    if (newFactor >= factor) {
      factor = newFactor
      id = item.id
      newValue = nextValue
    }
  }
  return { id, newValue, factor }
}

type IChange = {
  newValue: number
  factor: number
  quantities: Record<string, number>
  grouper: string | null
}

function incrementOrDecrementOrder(
  allItems: Record<
    string,
    {
      id: string
      quantity: number
      colliSize: number
      suggestedOrder: number
      forecast: number
      minimumOrderQuantity: number
      secondaryMoq: number
      secondaryMoqGrouper: string | undefined
      projectedStockAtArrival: number
      weeksInPurchasePeriod: number
    }[]
  >,
  increment: boolean,
  noMoqChange: boolean,
  cache: Record<string, IChange>,
  respectMoq: boolean,
  ignoreColliSize: boolean,
) {
  const defaultChange: IChange = !increment
    ? { newValue: 0, factor: Number.NEGATIVE_INFINITY, quantities: {}, grouper: null }
    : { newValue: 0, factor: Number.POSITIVE_INFINITY, quantities: {}, grouper: null }
  let change = defaultChange
  for (const [grouper, items] of Object.entries(allItems)) {
    const cachedChange = cache[grouper]
    if (cachedChange && cachedChange.factor < change.factor === increment) {
      change = cachedChange
      delete cache[grouper]
      continue
    }
    const quantities = Object.fromEntries(
      items.map((item) => [item.id, item.quantity] as [string, number]),
    )
    const moq = respectMoq
      ? items.reduce((max, item) => Math.max(max, item.secondaryMoq ?? 1), 1)
      : 1
    let quantity = sumBy(items, "quantity")
    let groupChange = defaultChange

    if (increment) {
      if (quantity < moq) {
        while (quantity < moq && !noMoqChange) {
          const { id, newValue, factor } = findIncrementOrderOnItemLevel(
            items,
            quantities,
            respectMoq,
            ignoreColliSize,
          )
          const oldQuantity = quantities[id] ?? 0
          quantities[id] = newValue
          quantity += newValue - oldQuantity

          groupChange = { newValue, factor, quantities, grouper }
        }
      } else {
        const { id, newValue, factor } = findIncrementOrderOnItemLevel(
          items,
          quantities,
          respectMoq,
          ignoreColliSize,
        )
        quantities[id] = newValue
        groupChange = { newValue, factor, quantities, grouper }
      }
    } else {
      if (quantity > 0) {
        const { id, newValue, factor } = findDecrementOrderOnItemLevel(
          items,
          quantities,
          respectMoq,
          ignoreColliSize,
        )
        if (quantity - ((quantities[id] ?? 0) - newValue) < moq) {
          while (quantity > 0 && !noMoqChange) {
            const { id, newValue, factor } = findDecrementOrderOnItemLevel(
              items,
              quantities,
              respectMoq,
              ignoreColliSize,
            )
            const oldQuantity = quantities[id] ?? 0
            quantities[id] = newValue
            quantity += newValue - oldQuantity
            if (newValue - oldQuantity > 0) {
              throw new Error("Should not happen")
            }
            groupChange = { newValue, factor, quantities, grouper }
          }
        } else {
          quantities[id] = newValue
          groupChange = { newValue, factor, quantities, grouper }
        }
      }
    }
    if (increment ? groupChange.factor <= change.factor : groupChange.factor >= change.factor) {
      change = groupChange
    }
    cache[grouper] = groupChange
  }
  // `null`, not "", marks "no candidate": "" is a valid group key when the grouping property is empty
  if (change.grouper === null) {
    return false
  }
  allItems[change.grouper]?.forEach((item) => {
    item.quantity = change.quantities[item.id] ?? item.quantity
  })
  return true
}

export function adjustPurchaseOrder(
  target: number,
  skus: IPurchaseOrderItem[],
  exact: boolean = false,
  respectMoq: boolean = true,
  ignoreColliSize: boolean = false,
  weeksInPurchasePeriod: number = 0,
) {
  const allItems = skus.map((sku) => ({
    id: sku.skuId + "//" + sku.warehouseId,
    skuId: sku.skuId,
    warehouseId: sku.warehouseId,
    quantity: sku.item.quantity,
    colliSize: sku.settings?.colliSize ?? 1,
    suggestedOrder: sku.suggestedOrderRawQuantity,
    forecast: sku.forecastsPurchasePeriod,
    minimumOrderQuantity: sku.settings?.minimumOrderQuantity ?? 1,
    secondaryMoq: skus.length > 1 ? (sku.settings?.secondaryMoq ?? 0) : 0, // If there is only one sku, we allow for individual adjustment
    secondaryMoqGrouper: sku.secondaryMoqGrouper,
    projectedStockAtArrival: sku.projectedStockAtArrival ?? 0,
    weeksInPurchasePeriod: sku.weeksInPurchasePeriod ?? weeksInPurchasePeriod,
    purchasePeriodLostSales: Math.ceil(sku.purchasePeriodLostSales ?? 0),
  }))

  target = Math.max(0, target)
  let quantity = sumBy(allItems, "quantity")
  const increment = target > quantity

  // Phase 1: Cover lost sales first
  // Uses a MinPriorityQueue with factor = (lostSales - nextQuantity) / max(forecast, 1).
  // Lower factor = more lost sales resolved relative to demand = higher priority.
  // Factor is evaluated at *next* quantity (after colli/MOQ rounding) to avoid
  // over-prioritizing SKUs with low forecasts and high MOQ.
  // Stops when target reached or best factor <= 0 (all lost sales covered).
  if (increment) {
    const computeFactor = (item: (typeof allItems)[0]) => {
      const nextValue = getNextValue(
        item.quantity,
        ignoreColliSize ? 1 : item.colliSize,
        respectMoq ? item.minimumOrderQuantity : 1,
        1,
        false,
      )
      const denominator = Math.max(item.forecast, 1)
      return { factor: (item.purchasePeriodLostSales - nextValue) / denominator, nextValue }
    }

    const lostSalesQueue = new MinPriorityQueue<{
      item: (typeof allItems)[0]
      factor: number
      nextValue: number
    }>((entry) => -entry.factor) // negate so highest factor (most unresolved) dequeues first

    for (const item of allItems) {
      const { factor, nextValue } = computeFactor(item)
      if (factor >= 0) {
        lostSalesQueue.enqueue({ item, factor, nextValue })
      }
    }

    while (quantity < target && lostSalesQueue.size() > 0) {
      const entry = lostSalesQueue.dequeue()
      if (!entry || entry.factor < 0) break

      const added = entry.nextValue - entry.item.quantity
      entry.item.quantity = entry.nextValue
      quantity += added

      const { factor, nextValue } = computeFactor(entry.item)
      if (factor >= 0) {
        lostSalesQueue.enqueue({ item: entry.item, factor, nextValue })
      }
    }
  }

  // Phase 2: WoH balancing for remaining increment, or all decrement
  let cache: Record<string, IChange> = {}
  const allItemsByGrouper = groupBy(allItems, "secondaryMoqGrouper")
  while (quantity !== target && quantity < target === increment) {
    const changed = incrementOrDecrementOrder(
      allItemsByGrouper,
      increment,
      false,
      cache,
      respectMoq,
      ignoreColliSize,
    )
    if (!changed) {
      break
    }
    quantity = sumBy(allItems, "quantity")
  }

  cache = {} // The cache needs to be reset. Since the increment/decrement changes the value
  while (exact && quantity !== target && quantity > target === increment) {
    incrementOrDecrementOrder(
      allItemsByGrouper,
      !increment,
      true,
      cache,
      respectMoq,
      ignoreColliSize && skus.length === 1,
    )
    if (quantity === sumBy(allItems, "quantity")) {
      break
    }
    quantity = sumBy(allItems, "quantity")
  }

  return allItems.map((item) => ({
    skuId: item.skuId,
    warehouseId: item.warehouseId,
    quantity: item.quantity,
  }))
}
